// src/lib/sessionPolicy.js
// ───────────────────────────────────────────────────────────────────────────
// Política de sesión: cuánto dura la inactividad antes del auto-logout, y a qué
// pantalla volver tras re-ingresar.
//
// Dos fuentes para el temporizador (en orden de prioridad):
//   1) TIPO DE ESTACIÓN (por COMPUTADORA, guardado en localStorage de ESTE navegador):
//      diseño / administración / producción. Útil en máquinas compartidas: una PC de
//      producción mantiene el intervalo corto sin importar quién entre.
//   2) ROL del usuario conectado (si la máquina no tiene estación fijada).
//
// Producción = corto (estaciones compartidas, higiene de identidad). Diseño = medio.
// Administración = largo.
// ───────────────────────────────────────────────────────────────────────────

const STATION_KEY = 'cb_station_type';
const LAST_PATH_KEY = 'cb_last_path';

// Tipos de estación (se fijan por máquina/navegador).
// "Tablet personal": dispositivo asignado a UN operador con biometría de Android —
// la higiene de identidad la cubre el lock screen, no el auto-logout. El timeout de
// 12 h es red de seguridad si la tablet se pierde sin bloquear, no higiene diaria.
export const STATION_TYPES = {
  design:     { id: 'design',     label: 'Diseño',          timeoutMin: 20 },
  admin:      { id: 'admin',      label: 'Administración',  timeoutMin: 30 },
  production: { id: 'production', label: 'Producción',      timeoutMin: 8  },
  tablet:     { id: 'tablet',     label: 'Tablet personal', timeoutMin: 720 },
};

export function getStationType() {
  try {
    const v = localStorage.getItem(STATION_KEY);
    return STATION_TYPES[v] ? v : '';
  } catch (_) { return ''; }
}

export function setStationType(type) {
  try {
    if (type && STATION_TYPES[type]) localStorage.setItem(STATION_KEY, type);
    else localStorage.removeItem(STATION_KEY);
  } catch (_) { /* localStorage no disponible */ }
}

// Minutos por ROL (cuando la máquina NO tiene estación fijada). Si el usuario tiene
// varios roles, se toma el MÁS LARGO (menos disruptivo) — salvo que la estación mande.
const ROLE_TIMEOUTS = [
  { roles: ['Admin', 'Project Manager'], min: 30 },
  { roles: ['Diseño', 'Diseñador Freelance'], min: 20 },
  { roles: ['Operador', 'Instalador', 'Estación'], min: 8 },
];
const DEFAULT_MIN = 15;

export function roleTimeoutMin(user) {
  const userRoles = (user && user.roles) || [];
  let best = 0;
  ROLE_TIMEOUTS.forEach(({ roles, min }) => {
    if (roles.some((r) => userRoles.includes(r)) && min > best) best = min;
  });
  return best || DEFAULT_MIN;
}

// Minutos efectivos: la ESTACIÓN (por máquina) tiene prioridad; si no, el ROL.
export function resolveTimeoutMin(user) {
  const station = getStationType();
  if (station) return STATION_TYPES[station].timeoutMin;
  return roleTimeoutMin(user);
}

export function resolveTimeoutMs(user) {
  return resolveTimeoutMin(user) * 60 * 1000;
}

// De dónde sale el tiempo (para mostrarlo en la UI).
export function timeoutSource() {
  return getStationType() ? 'station' : 'role';
}

// ── Última pantalla: volver donde quedó tras re-login ─────────────────────────
export function saveLastPath(path) {
  try {
    if (path && !path.startsWith('/login')) localStorage.setItem(LAST_PATH_KEY, path);
  } catch (_) { /* noop */ }
}

export function getLastPath() {
  try { return localStorage.getItem(LAST_PATH_KEY) || ''; }
  catch (_) { return ''; }
}
